/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx; this change only swaps an icon import, converting to .tsx is a separate migration */
/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import {
  Flex,
  IconButton,
  Button,
  Input,
  Icon,
  Text,
  Tag,
  Textarea,
  Box,
} from '@chakra-ui/react';
import { Like } from '@/assets/icons/line';
import { Like as LikeSolid } from '@/assets/icons/bold';
import { Reload } from '@/assets/icons/ai';
import { GradientSpinner } from '@/components/ai/GradientSpinner';
import { useRetry } from '@/hooks/use-retry';
import {
  useAiMessageFeedbackMutation,
  useAiMessageGenerationStatusMutation,
  useRetryAiMessageMutation,
} from '@/store/query/chatgpt';
import { useShowToast } from '@/hooks/useShowToast';
import { ChevronLeft, ChevronRight, X } from '@kula-ai/icons-core';

const AIBlockComponent = ({ ...props }) => {
  const {
    aiMessages: actualAIMessage,
    channel = 'email',
    handleAIChange,
    getCurrentMessage,
  } = props?.extension?.options;

  const [aiMessageText, setAiMessageText] = useState();
  const [isAIRetrying, setAIRetrying] = useState(false);
  const [startRetry, setStartRetry] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedAIMessageID, setSelectedAIMessageID] = useState(null);
  const [retryId, setRetryId] = useState(null);
  const [feedbackValue, setFeedbackValue] = useState();
  const [isAIFeedbackLoading, setAIFeedbackLoading] = useState(false);
  const [aiRetryState, setAIRetryState] = useState(false);
  const [aiMessages, setAIMessages] = useState(actualAIMessage);
  const [activeMessage, setActiveMessage] = useState({});

  const [aifeedback, { isloading: isAiFeedbackDataLoading }] =
    useAiMessageFeedbackMutation();
  const [retryAiMessage, { isLoading: isRetrying }] =
    useRetryAiMessageMutation();
  const [generationStatus] = useAiMessageGenerationStatusMutation();

  const toast = useShowToast();

  const getCurrentAiMessage = useCallback((aiMessages) => {
    let currentMessage = aiMessages?.find((message) => message.current);
    if (!currentMessage && aiMessages?.length > 0) {
      currentMessage = aiMessages[aiMessages?.length - 1];
    }
    const selectedAiMessage =
      currentMessage?.edited_text || currentMessage?.generated_text;
    setSelectedAIMessageID(currentMessage?.id);
    setShowFeedback(currentMessage?.feedback);
    setFeedbackValue(currentMessage?.feedback_text);
    setAiMessageText(selectedAiMessage);
    setActiveMessage(currentMessage);
    getCurrentMessage(currentMessage); // It is a callback function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedAiMessage =
      activeMessage?.edited_text || activeMessage?.generated_text;
    setSelectedAIMessageID(activeMessage?.id);
    setAiMessageText(selectedAiMessage);
    getCurrentMessage(activeMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMessage]);

  useEffect(() => {
    getCurrentAiMessage(aiMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTextAreaInputChange = (e) => {
    setAiMessageText(e?.target?.value);
    handleAIChange(e?.target?.value);
  };

  const onRetry = async () => {
    // If some Retry already happening Don't allow one more retry
    if (isAIRetrying) return;
    setAIRetrying(true);
    // If Retry limit exceeded, Don't Retry
    if (aiMessages?.length >= 3) return;
    setStartRetry(false);
    setShowFeedback(false);
    const result = await retryAiMessage({
      aiMessageId: selectedAIMessageID,
    });
    if (result?.data?.regenerated_message?.id) {
      setRetryId(result?.data?.regenerated_message?.id);
      setStartRetry(true);
    } else {
      toast({
        title: `Failed to regenerate message. Try again!`,
        status: 'error',
        position: 'bottom-left',
        isClosable: true,
      });
    }
    setAIRetrying(false);
  };

  const onAIRetryStatus = async ({ setAIRetrying, retryId }) => {
    setAIRetrying(true);
    return new Promise(async (resolve, reject) => {
      try {
        const result = await generationStatus({ aiMessageId: retryId });
        if (result?.data?.ai_message?.status !== 'success') {
          reject(result?.data);
          return;
        }
        resolve(result?.data);
      } catch {}
    });
  };

  const {
    inProgress: aiRetryInProgress,
    data: aiRetryData,
    error: aiRetryError,
  } = useRetry({
    fn: onAIRetryStatus,
    args: { setAIRetrying, retryId },
    retryOptions: { retries: 10, factor: 1 },
    start: startRetry,
  });

  useEffect(() => {
    if (aiRetryInProgress || (aiRetryData === null && aiRetryError === null))
      return;

    if (aiRetryData && retryId === aiRetryData?.ai_message?.id) {
      setAIRetryState(aiRetryData);
      setAIRetrying(false);
    }
    if (aiRetryError && retryId === aiRetryError?.ai_message?.id) {
      setAIRetryState(aiRetryError);
      setAIRetrying(false);
      if (aiRetryError?.ai_message?.status !== 'success') {
        toast({
          title: 'Failed to regenerate message. Try again!',
          status: 'error',
          position: 'top-right',
          isClosable: true,
        });
      }
    }
  }, [aiRetryInProgress, aiRetryData, aiRetryError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!aiRetryState?.ai_message?.id) return;
    // TODO if no id incase of failed API call, update step with id information we have

    const newAiMessages = [
      ...aiMessages,
      {
        ...aiRetryState?.ai_message,
        feedback: null,
        feedback_text: null,
        current: false,
      },
    ];
    setAIMessages(newAiMessages);
    getCurrentAiMessage(newAiMessages);
    setSelectedAIMessageID(aiRetryState?.ai_message?.id);

    setActiveMessage(aiRetryState?.ai_message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRetryState]);

  const renderInputArea = () => (
    <Flex flex="1" width="100%">
      <Flex bg="#FFFFFF" flexDir="column" mr="10px" width="100%">
        <Textarea
          maxlength={channel !== 'email' && '500'}
          bg="#FFF"
          placeholder="You can add your own candidate personalization here..."
          padding="5px"
          fontSize="14px"
          value={aiMessageText}
          overflow="hidden"
          onChange={handleTextAreaInputChange}
          _focus={{ outline: 'none' }}
          overflowY="auto"
          minHeight="100%"
        />
      </Flex>
      <Flex flexDirection="column">
        <IconButton
          variant="outline"
          h="24px"
          w="24px"
          icon={<Icon as={X} w="20px" h="20px" />}
          bg="#fff"
          _hover={{ background: '#fff' }}
          onClick={() => props?.deleteNode()}
        />
        {renderActionCTA()}
      </Flex>
    </Flex>
  );

  const onFeedback = async (feedback) => {
    try {
      if (isAIFeedbackLoading && isAiFeedbackDataLoading) return;
      setAIFeedbackLoading(true);
      const feedBackData = {
        feedback,
        aiMessageId: selectedAIMessageID,
        ...(!feedback && { feedbackText: feedbackValue }),
      };
      const res = await aifeedback(feedBackData);
      if (res?.error?.length) {
        toast({
          title: `We couldn't save your feedback at this moment! Please try again`,
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
      } else {
        setShowFeedback(feedback);
        setActiveMessage({
          ...activeMessage,
          ...{ feedback: feedback, feedback_text: feedBackData.feedbackText },
        });

        const newAiMessages = aiMessages.map((aiMessage) => {
          if (aiMessage.id === activeMessage.id) {
            return {
              ...activeMessage,
              ...{
                feedback: feedback,
                feedback_text: feedBackData.feedbackText,
              },
            };
          }
          return aiMessage;
        });
        setAIMessages([...newAiMessages]);
        toast({
          title: 'Thanks for your feedback!',
          position: 'bottom-left',
          status: 'success',
          isClosable: true,
        });
      }
      setAIFeedbackLoading(false);
    } catch {
      setAIFeedbackLoading(false);
    }
  };

  const renderActionCTA = () => (
    <Flex
      bg="#FFFFFF"
      borderRadius="8px"
      mt="5px"
      padding="8px 5px"
      gap="10px"
      alignItems="center"
      direction="column"
    >
      {activeMessage?.feedback !== null &&
      activeMessage?.feedback !== undefined &&
      showFeedback ? (
        <Icon
          id="active-like"
          as={LikeSolid}
          width="24px"
          height="24px"
          cursor="pointer"
          color="#5551FF"
        />
      ) : (
        <Icon
          id="like"
          as={Like}
          width="24px"
          height="24px"
          cursor="pointer"
          onClick={() => {
            onFeedback(true);
          }}
        />
      )}
      {activeMessage?.feedback !== null &&
      activeMessage?.feedback !== undefined &&
      !showFeedback ? (
        <Icon
          id="active-dislike"
          as={LikeSolid}
          width="24px"
          height="24px"
          transform="rotate(180deg)"
        />
      ) : (
        <Icon
          id="dislike"
          as={Like}
          transform="rotate(180deg)"
          width="24px"
          height="24px"
          cursor="pointer"
          onClick={async () => {
            await onFeedback(false);
            setActiveMessage({
              ...activeMessage,
              ...{ feedback: false, feedback_text: '' },
            });
            setFeedbackValue('');
          }}
        />
      )}
      <Icon
        as={Reload}
        onClick={onRetry}
        width="20px"
        height="20px"
        cursor={aiMessages?.length < 3 ? 'pointer' : 'not-allowed'}
        opacity={aiMessages?.length < 3 ? 1 : 0.3}
      />
    </Flex>
  );

  const goPrevStep = (id) => {
    try {
      const current = aiMessages?.findIndex((el) => el?.id === id);
      if (current === 0) return;
      setSelectedAIMessageID(aiMessages?.[current - 1]?.id);
      setActiveMessage(aiMessages?.[current - 1]);
      setShowFeedback(aiMessages?.[current - 1].feedback);
    } catch {}
  };

  const goNextStep = (id) => {
    try {
      const current = aiMessages?.findIndex((el) => el?.id === id);
      if (current === aiMessages?.length - 1) return;
      setSelectedAIMessageID(aiMessages?.[current + 1]?.id);
      setActiveMessage(aiMessages?.[current + 1]);
      setShowFeedback(aiMessages?.[current + 1].feedback);
    } catch {}
  };

  const renderGeneratedAIMessages = () => {
    return (
      <>
        {aiMessages?.length > 1 && !(isAIRetrying || isRetrying) && (
          <Flex bg="#EDF5FF" h="36px" px="20px" alignItems="center">
            <Text flex="1">
              {aiMessages?.length} AI Messages{' '}
              {/* `Current` label will be only shown for ai message that is manually approved by the user.  */}
              {aiMessages?.find((mes) => mes?.current === true)?.id ===
                selectedAIMessageID && (
                <Tag
                  size="md"
                  bg="#FFFFFF"
                  color="#595959"
                  fontSize="12px"
                  borderRadius="100px"
                >
                  Current
                </Tag>
              )}
            </Text>

            <Flex alignItems="center">
              <Icon
                as={ChevronLeft}
                cursor={
                  aiMessages?.findIndex(
                    (el) => el?.id === selectedAIMessageID
                  ) === 0
                    ? 'not-allowed'
                    : 'pointer'
                }
                opacity={
                  aiMessages?.findIndex(
                    (el) => el?.id === selectedAIMessageID
                  ) === 0
                    ? 0.3
                    : 1
                }
                onClick={() => goPrevStep(selectedAIMessageID)}
              />
              <Text px="4px">
                {aiMessages?.findIndex((el) => el?.id === selectedAIMessageID) +
                  1}
                /{aiMessages?.length}
              </Text>
              <Icon
                as={ChevronRight}
                cursor={
                  aiMessages?.findIndex(
                    (el) => el?.id === selectedAIMessageID
                  ) +
                    1 ===
                  aiMessages?.length
                    ? 'not-allowed'
                    : 'pointer'
                }
                opacity={
                  aiMessages?.findIndex(
                    (el) => el?.id === selectedAIMessageID
                  ) ===
                  aiMessages?.length - 1
                    ? 0.3
                    : 1
                }
                onClick={() => goNextStep(selectedAIMessageID)}
              />
            </Flex>
          </Flex>
        )}
      </>
    );
  };

  const handleFeedback = (e) => {
    setFeedbackValue(e?.target?.value);
  };

  const renderFeedback = () => (
    <>
      {!isAIRetrying &&
        showFeedback !== null &&
        !showFeedback &&
        !activeMessage?.feedback_text && (
          <Flex p="10px 0px" flexDir="column" gap="10px" width="100%">
            <Input
              placeholder="Please give us feedback"
              bg="#FFFFFF"
              value={feedbackValue}
              onChange={handleFeedback}
              fontSize="12px"
            />
            <Button
              w="fit-content"
              ml="auto"
              variant="secondary"
              onClick={() => onFeedback(false)}
            >
              Share
            </Button>
          </Flex>
        )}
    </>
  );

  const isStepLoading =
    activeMessage?.status === 'queued' ||
    activeMessage?.status === 'in_progress';

  return (
    <NodeViewWrapper>
      <Flex
        bg="#FBF9F6"
        borderRadius="8px"
        border="1px solid #F0E8DD"
        flexDirection="column"
        alignItems={isAIRetrying || isStepLoading ? 'center' : 'flex-start'}
        data-drag-handle
        mt="5px"
        p="8px"
        gap="5px"
      >
        {isAIRetrying || isStepLoading ? (
          <Flex direction="column" alignItems="center" py="10px">
            <GradientSpinner
              title="Trying again"
              description="We're getting that preview ready for you. Hang in there!"
            />
            <Text fontWeight="semiBold">Giving it another go</Text>
            <Text w="238px" textAlign="center" color="secondary-500">
              One more try, and we'll have that perfect personalisation ready!
              Give us a second.
            </Text>
          </Flex>
        ) : (
          <>
            {activeMessage?.status === 'failed' && (
              <>
                <Flex borderRadius="8px" mx="auto" my="10px">
                  <Box textAlign="center">
                    <Text color="#161616" fontWeight="semiBold" fontSize="md">
                      Failed to generate
                    </Text>
                    <Text color="#8D8D8D" fontWeight="400" fontSize="sm">
                      Looks like we hit a snag generating your message. Give it
                      one more go or add your own personalization?
                    </Text>
                  </Box>
                </Flex>
              </>
            )}
            <>
              {renderInputArea()}
              {renderFeedback()}
            </>
          </>
        )}
      </Flex>
      {!isAIRetrying && renderGeneratedAIMessages()}
    </NodeViewWrapper>
  );
};

export default AIBlockComponent;
